import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { eventsTrailApi } from '@/domain/events/api';
import { buildTrailFilterParams, buildTrailListQuery, type TrailFilters } from '@/domain/events/params';
import type { TrailSource } from '@/domain/events/schemas';
import { queryOptions } from '@/services/query';
import { eventsTrailKeys } from '@/domain/events/queries/keys';

type TrailPage = Awaited<ReturnType<typeof eventsTrailApi.getTrail>>;

export const useEventsTrail = (
  companyId: string,
  source: TrailSource,
  filters: TrailFilters,
  enabled: boolean,
  entityId?: string,
) =>
  useInfiniteQuery<TrailPage, AxiosError>({
    queryKey: eventsTrailKeys.list(companyId, source, entityId, buildTrailFilterParams(filters)),
    queryFn: ({ pageParam = '' }) =>
      eventsTrailApi.getTrail({
        path: { source },
        query: buildTrailListQuery(filters, pageParam as string, entityId),
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage || undefined,
    refetchOnWindowFocus: false,
    enabled,
  });

export const eventsTrailFiltersQuery = (companyId: string, source: TrailSource, enabled: boolean) =>
  queryOptions({
    queryKey: eventsTrailKeys.filters(companyId, source),
    queryFn: () => eventsTrailApi.getTrailFilters({ path: { source } }),
    enabled,
  });

export const useEventsTrailFilters = (companyId: string, source: TrailSource, enabled: boolean) =>
  useQuery(eventsTrailFiltersQuery(companyId, source, enabled));
